import React from "react";
import { Field, ErrorMessage } from "formik";
import { Plan } from "../dataTypes";

interface TrialCreditsTabProps {
  values: Plan;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
  onPrevious: () => void;
  onNext: () => void;
}

const TrialCreditsTab: React.FC<TrialCreditsTabProps> = ({
  values,
  setFieldValue,
  onPrevious,
  onNext,
}) => {
  const toggleTrial = () => {
    const newActive = !values.trialPeriod.active;
    setFieldValue('trialPeriod.active', newActive, true);
    if (newActive && values.trialPeriod.days === 0) {
      setFieldValue('trialPeriod.days', 14, true);
    }
  };

  const toggleCredits = () => {
    const newActive = !values.credits.active;
    setFieldValue('credits.active', newActive, true);
    if (newActive && values.credits.amount === 0) {
      setFieldValue('credits.amount', 100, true);
    }
  };

  return (
    <div className="space-y-8">
      {/* Trial Period */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Trial Period</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Offer a free trial period to attract customers
            </p>
          </div>
          <button
            type="button"
            onClick={toggleTrial}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
              values.trialPeriod.active 
                ? 'bg-blue-600' 
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              values.trialPeriod.active ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {values.trialPeriod.active && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Trial Duration (Days) *
            </label>
            <Field
              name="trialPeriod.days"
              type="number"
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
              min="1"
              max="365"
            />
            <ErrorMessage name="trialPeriod.days" component="div" className="text-red-500 text-sm mt-2" />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Set the number of days for the free trial period (1-365 days)
            </p>
          </div>
        )}
      </div>

      {/* Credits */}
      <div className="border border-gray-200 dark:border-gray-600 rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Credits System</h3>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Include credits for additional features or usage
            </p>
          </div>
          <button
            type="button"
            onClick={toggleCredits}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
              values.credits.active 
                ? 'bg-blue-600' 
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              values.credits.active ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>

        {values.credits.active && (
          <div className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Credit Type *
                </label>
                <Field
                  as="select"
                  name="credits.creditType"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                >
                  <option value="recurring">Recurring Credits</option>
                  <option value="onetime">One-time Credits</option>
                </Field>
                <ErrorMessage name="credits.creditType" component="div" className="text-red-500 text-sm mt-2" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Credit Amount *
                </label>
                <Field
                  name="credits.amount"
                  type="number"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                  min="1"
                />
                <ErrorMessage name="credits.amount" component="div" className="text-red-500 text-sm mt-2" />
              </div>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {values.credits.creditType === 'recurring' 
                ? 'Credits will be replenished each billing cycle'
                : 'One-time credits provided upon subscription'
              }
            </p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onPrevious}
          className="px-8 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors duration-200 shadow-sm hover:shadow-md"
        >
          Continue to Rebilling
        </button>
      </div>
    </div>
  );
};

export default TrialCreditsTab;